import { useEffect, useState } from "react";
import { ChevronDown, Users } from "lucide-react";
import { api } from "../lib/api";

const META = {
  yes: { label: "Going", dot: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700" },
  maybe: { label: "Maybe", dot: "bg-amber-500", chip: "bg-amber-50 text-amber-700" },
  no: { label: "Can't make it", dot: "bg-red-500", chip: "bg-red-50 text-red-700" },
};
const PREVIEW = 6;

const initials = (name) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");

const GuestRow = ({ guest, response }) => (
  <li data-testid="guest-list-entry" className="flex gap-3 py-3">
    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold ${META[response].chip}`}>{initials(guest.name)}</span>
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <span data-testid="guest-list-entry-name" className="font-semibold text-slate-900">{guest.name}</span>
        {guest.extras > 0 && (
          <span data-testid="guest-list-entry-extras" className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
            +{guest.extras} guest{guest.extras > 1 ? "s" : ""}
          </span>
        )}
      </div>
      {guest.comment && <p data-testid="guest-list-entry-comment" className="mt-0.5 break-words text-sm text-slate-500">“{guest.comment}”</p>}
    </div>
  </li>
);

const Group = ({ group }) => {
  const [expanded, setExpanded] = useState(false);
  const meta = META[group.response];
  const shown = expanded ? group.guests : group.guests.slice(0, PREVIEW);
  return (
    <div data-testid={`guest-list-group-${group.response}`} className="py-4 first:pt-0 last:pb-0">
      <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-slate-500">
        <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
        {meta.label}
        <span data-testid={`guest-list-count-${group.response}`} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs tracking-normal text-slate-700">{group.people_count ?? group.count}</span>
      </h3>
      {group.count === 0 ? (
        <p className="mt-2 text-sm text-slate-400">No one yet.</p>
      ) : (
        <ul className={`divide-y divide-slate-100 ${expanded ? "max-h-96 overflow-y-auto pr-1" : ""}`}>
          {shown.map((g, i) => <GuestRow key={`${g.name}-${i}`} guest={g} response={group.response} />)}
        </ul>
      )}
      {group.count > PREVIEW && (
        <button data-testid={`guest-list-toggle-${group.response}`} onClick={() => setExpanded((v) => !v)} className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:text-blue-800">
          {expanded ? "Show less" : `Show all ${group.count}`}
          <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
        </button>
      )}
    </div>
  );
};

export const GuestList = ({ slug, refreshKey }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/public/events/${slug}/guests`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [slug, refreshKey]);

  if (!data || !data.groups.length) return null;
  return (
    <section data-testid="guest-list" className="rise mt-6 rounded-2xl border border-slate-200 bg-white/95 p-5 shadow-sm backdrop-blur sm:p-6">
      <h2 className="mb-4 flex items-center gap-2 text-xl font-bold tracking-tight text-slate-900">
        <Users className="h-5 w-5 text-blue-700" /> Who's coming
      </h2>
      <div className="divide-y divide-slate-100">
        {data.groups.map((g) => <Group key={g.response} group={g} />)}
      </div>
    </section>
  );
};
